import React, { useState, useEffect, useRef } from "react";
// @ts-ignore
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./style.scss";

interface DateRangePickerProps {
  onDateChange?: (fromDate: string, toDate: string) => void;
  handleDatechangeAPICall?: (e: React.FocusEvent<HTMLInputElement>) => void;
  FromDate?: string;
  ToDate?: string;
  min?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateChange,
  FromDate = "dd-mm-yyyy",
  ToDate = "dd-mm-yyyy",
  min,
  handleDatechangeAPICall,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const parseDateString = (dateStr?: string | Date) => {
    if (!dateStr || dateStr === "dd-mm-yyyy") return undefined;

    if (dateStr instanceof Date) return dateStr;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // months are 0-based
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }

    // fallback: try parsing as ISO or JS date string
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? undefined : d;
  };

  const defaultValue = [
    {
      startDate: parseDateString(FromDate),
      endDate: parseDateString(ToDate),
      key: "selection",
    },
  ];
  const [range, setRange] = useState(defaultValue);
  const [displayRange, setDisplayRange] = useState(defaultValue);

  const formatDate = (
    date?: Date,
    type?: "DD-MM-YYYY" | "YYYY-MM-DD" | "DD/MM/YYYY"
  ) => {
    if (!date) return "";

    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();

    switch (type) {
      case "YYYY-MM-DD":
        return `${y}-${m}-${d}`;
      case "DD/MM/YYYY":
        return `${d}/${m}/${y}`;
      case "DD-MM-YYYY":
      default:
        return `${d}-${m}-${y}`;
    }
  };

  const handleChange = (item: any) => {
    setDisplayRange([item.selection]);
  };

  const handleApply = () => {
    const { startDate, endDate } = displayRange[0];
    if (startDate && endDate && onDateChange) {
      setRange(displayRange);
      onDateChange(
        formatDate(startDate, "DD-MM-YYYY"),
        formatDate(endDate, "DD-MM-YYYY")
      );
    }
    setOpen(false);
  };

  const handleCancel = () => {
    const { startDate, endDate } = range[0];
    if (!startDate && !endDate && onDateChange) {
      setDisplayRange(defaultValue);
    }
    setOpen(false);
  };

  const displayValue =
    range[0].startDate && range[0].endDate
      ? `${formatDate(range[0].startDate, "DD-MM-YYYY")} to ${formatDate(range[0].endDate, "DD-MM-YYYY")}`
      : "dd-mm-yyyy to dd-mm-yyyy";

  // close popup on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        const { startDate, endDate } = range[0];
        if (!startDate && !endDate && onDateChange) {
          setDisplayRange(defaultValue);
        }
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="date-range-picker-container" ref={wrapperRef}>
      <div className="date-range-input-wrapper"
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        <div className="date-range-display">{displayValue}</div>
      </div>

      {open && (
        <div className="date-range-popup">
          <DateRange
            editableDateInputs={true}
            onChange={handleChange}
            moveRangeOnFirstSelection={false}
            ranges={[
              {
                startDate: displayRange[0].startDate
                  ? displayRange[0].startDate
                  : new Date(),
                endDate: displayRange[0].endDate
                  ? displayRange[0].endDate
                  : new Date(),
                key: "selection",
              },
            ]}
            months={1}
            direction="horizontal"
            minDate={min ? new Date(min) : new Date(2000, 0, 1)}
            maxDate={new Date(2030, 11, 31)}
          />

          <div className="date-range-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="apply-btn" onClick={handleApply}>
              Select
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
